// The Measure tool in the 3D view (docs/claude/rendering.md, *Measure in 3D*):
// two clicks on what the view shows (else the floor; a corner near the pointer
// snaps, tools3d.ts) read the distance between
// them and their gaps along X, Y and Z; a third click starts again. Nothing in
// the drawing changes.
import { drawingDimStyle } from '../dimFormat';
import { measureReadout } from '../measure3d';
import { type Pt3 } from '../handles';
import { state } from './core';
import { viewer3d } from './view3d';

/** The points clicked (none, one, or the two measured) and where the pointer is. */
let pts: Pt3[] = [];
let hover: Pt3 | null = null;

/** How many points are down. */
export const measure3dCount = (): number => pts.length;

/** Let go of the measurement (Esc, another tool). */
export function resetMeasure3d(): void {
  if (!pts.length && !hover) return;
  pts = [];
  hover = null;
  viewer3d?.redraw();
}

/** A click with the Measure tool in 3D, at `p` (where it lands, snapped —
 *  tools3d.ts): the first point, the second (the measurement, which stays
 *  shown), or a new first. */
export function measure3dClick(p: Pt3): void {
  pts = pts.length === 1 ? [pts[0], p] : [p];
  viewer3d?.redraw();
}

/** The pointer moving with the Measure tool in 3D, over `p`: the line follows
 *  it from the first point. */
export function measure3dHover(p: Pt3 | null): void {
  hover = p;
  if (pts.length === 1) viewer3d?.redraw();
}

/** Paint the measurement on the 3D overlay: its points, the line between them
 *  (to the pointer while the second is not down), and what it reads beside the
 *  line's middle. Lists it for the web cases (`data-measure`: `d|dx|dy|dz`;
 *  `data-measure-text`: its lines as shown, joined by ` | `). */
export function paintMeasure3d(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
  cv.dataset.measureText = '';
  if (state.tool !== 'measure' || state.viewTab !== '3d') { pts = []; hover = null; cv.dataset.measure = ''; return; }
  const v = viewer3d;
  const a = pts[0], b = pts[1] ?? hover;
  if (!v || !a) { cv.dataset.measure = ''; return; }
  const pa = v.project(a), pb = b ? v.project(b) : null;
  ctx.save();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#ffd166';
  ctx.fillStyle = '#ffd166';
  if (pa && pb) {
    ctx.setLineDash(pts[1] ? [] : [6, 4]);
    ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y); ctx.stroke();
    ctx.setLineDash([]);
  }
  for (const q of [pa, pts[1] ? pb : null]) if (q) { ctx.beginPath(); ctx.arc(q.x, q.y, 4, 0, Math.PI * 2); ctx.fill(); }
  if (b) {
    const r = measureReadout(a, b, drawingDimStyle(state.drawing));
    cv.dataset.measure = pts[1] ? `${r.d}|${r.dx}|${r.dy}|${r.dz}` : '';
    cv.dataset.measureText = pts[1] ? r.lines.join(' | ') : '';
    if (pa && pb) {
      ctx.font = '12px sans-serif';
      const w = Math.max(...r.lines.map((l) => ctx.measureText(l).width)) + 12, h = 36;
      const x = (pa.x + pb.x) / 2 + 10, y = (pa.y + pb.y) / 2 - h - 6;
      ctx.fillStyle = 'rgba(20, 22, 28, 0.85)';
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = '#e6eefc';
      ctx.textBaseline = 'top';
      r.lines.forEach((l, i) => ctx.fillText(l, x + 6, y + 4 + i * 15));
    }
  } else cv.dataset.measure = '';
  ctx.restore();
}
